import { useMemo, useState, useCallback } from 'react';
import {
	ActionItem as ActionItemRow,
	ActionItemsContent,
	Text,
	ActionItemsContentHeader,
	StatusLabelWrapper,
} from './ActionItems.style';

import { useConfettiOnComplete } from '../../../hooks/useConfettiOnComplete';
import type { ActionItem } from './ActionItems';
import { Label } from '../../../label/Label';
import { ProgressBar } from '../progressBar/ProgressBar';
import { getVariantFromPercent } from '../progressBar/progressUtils';
import type { LabelVariant } from '../../../label/LabelVariant';

type Props = {
	item: ActionItem;
	sequenceIndex: number;
	useDynamicColors?: boolean;
	inline?: boolean;
	confettiArmed?: boolean;
};

export const ActionItemRowComponent = ({
	item,
	sequenceIndex,
	useDynamicColors,
	inline,
	confettiArmed,
}: Props) => {
	const [el, setEl] = useState<HTMLDivElement | null>(null);

	const setRef = useCallback((node: HTMLDivElement | null) => {
		setEl(node);
	}, []);

	const { hasProgress, progress, variant } = useMemo(() => {
		const has = item.target !== undefined && item.value !== undefined && item.target > 0;

		if (!has) {
			return {
				hasProgress: false,
				progress: 0,
				variant: 'neutral' as LabelVariant,
			};
		}

		const rawPercent = ((item.value ?? 0) / (item.target ?? 1)) * 100;
		const displayPercent = Math.round(rawPercent);

		return {
			hasProgress: true,
			progress: Math.min(Math.max(rawPercent / 100, 0), 1),
			variant: getVariantFromPercent(displayPercent),
		};
	}, [item.target, item.value]);

	useConfettiOnComplete({
		id: item.id ?? item.text,
		progress,
		element: el,
		enabled: hasProgress && (confettiArmed ?? false),
		sequenceIndex,
		baseDelayMs: 300,
	});

	return (
		<ActionItemRow ref={setRef} $inline={inline ?? true}>
			<ActionItemsContent>
				<ActionItemsContentHeader>
					<Text $inline={inline ?? true}>{item.text}</Text>

					{inline === false && item.status ? (
						<StatusLabelWrapper>
							<Label size="large" variant={variant} rounded>
								{item.status}
							</Label>
						</StatusLabelWrapper>
					) : null}
				</ActionItemsContentHeader>
			</ActionItemsContent>

			{hasProgress ? (
				<ProgressBar
					value={item.value ?? 0}
					target={item.target ?? 1}
					targettype={item.targettype}
					inline={inline ?? true}
					useDynamicColors={useDynamicColors ?? false}
					showPercent
					showValueLabel
				/>
			) : null}
		</ActionItemRow>
	);
};
